(() => {
  const root = typeof window !== 'undefined' ? window : globalThis;

  function decodeHtml(value) {
    if (!value) {
      return '';
    }

    const textarea = root.document.createElement('textarea');
    textarea.innerHTML = String(value);
    return textarea.value;
  }

  function formatReviewDate(isoDate, locale) {
    if (!isoDate) {
      return '';
    }

    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat(locale || 'en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  }

  function normalizeReview(review, locale, fallbackImageUrl) {
    const displayName = review?.user?.display_name || review?.user?.displayName || 'Customer';
    const initial = displayName.trim().charAt(0).toUpperCase() || 'C';
    const images = Array.isArray(review?.images_data) ? review.images_data : [];
    const videos = Array.isArray(review?.videos_data) ? review.videos_data : [];
    const imageUrl = images[0]?.original_url || images[0]?.thumb_url || '';
    const videoThumbnail = videos[0]?.thumbnail_url || '';
    const hasMedia = Boolean(imageUrl || videoThumbnail);

    return {
      id: review?.id || '',
      score: Number(review?.score) || 0,
      content: decodeHtml(review?.content || ''),
      name: displayName,
      initial,
      date: formatReviewDate(review?.created_at || review?.createdAt, locale),
      verifiedBuyer: Boolean(review?.verified_buyer),
      imageUrl,
      videoThumbnail,
      hasMedia,
      displayImageUrl: imageUrl || videoThumbnail || fallbackImageUrl || '',
    };
  }

  function computeAverageScore(reviews) {
    if (!reviews.length) {
      return 0;
    }

    const total = reviews.reduce((sum, review) => sum + (Number(review.score) || 0), 0);
    return total / reviews.length;
  }

  function buildDistribution(reviews) {
    const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

    reviews.forEach((review) => {
      const score = Math.round(Number(review.score) || 0);

      if (distribution[score] !== undefined) {
        distribution[score] += 1;
      }
    });

    return distribution;
  }

  function parseStarDistribution(starDistribution) {
    if (!starDistribution || typeof starDistribution !== 'object') {
      return null;
    }

    const parsed = {
      1: Number(starDistribution['1']) || 0,
      2: Number(starDistribution['2']) || 0,
      3: Number(starDistribution['3']) || 0,
      4: Number(starDistribution['4']) || 0,
      5: Number(starDistribution['5']) || 0,
    };

    const total = [1, 2, 3, 4, 5].reduce((sum, rating) => sum + parsed[rating], 0);
    return total > 0 ? parsed : null;
  }

  function buildDistributionRows(distribution, fallbackPercents = {}) {
    const distributionTotal = [1, 2, 3, 4, 5].reduce(
      (sum, rating) => sum + (Number(distribution?.[rating]) || 0),
      0
    );

    if (distributionTotal > 0) {
      return [5, 4, 3, 2, 1].map((rating) => ({
        rating,
        percent: Math.round(((Number(distribution[rating]) || 0) / distributionTotal) * 100),
      }));
    }

    return [5, 4, 3, 2, 1].map((rating) => ({
      rating,
      percent: Number(fallbackPercents[rating]) || 0,
    }));
  }

  function registerHomeYotpoTestimonials() {
    if (!root.document || !root.Alpine) {
      return;
    }

    root.Alpine.data('homeYotpoTestimonials', (config = {}) => ({
      appKey: config.appKey || '',
      reviewsUrl: config.reviewsUrl || '',
      sourceId: config.sourceId || '',
      locale: config.locale || 'en-US',
      useYotpo: config.useYotpo !== false,
      useShopAppSummary: config.useShopAppSummary === true,
      yotpoSecret: config.yotpoSecret || '',
      fallbackRating: config.fallbackRating || '4.9',
      reviewCountTemplate: config.reviewCountTemplate || '__COUNT__ ratings',
      reviewCountOneTemplate: config.reviewCountOneTemplate || '',
      fallbackDistribution: config.fallbackDistribution || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
      fallbackImageUrl: config.fallbackImageUrl || '',
      headerReviewCountTemplate: config.headerReviewCountTemplate || '{{ count }} Reviews',
      ratingLabelTemplate: config.ratingLabelTemplate || '{{ rating }} out of 5 stars',
      loadingLabel: config.loadingLabel || 'Loading reviews',
      activeIndex: 0,
      canNext: true,
      canPrev: false,
      loading: true,
      loadError: false,
      reviews: [],
      totalReviews: 0,
      averageScore: 0,
      distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
      countSource: '',
      displayReviewCountLabel: '',

      init() {
        this.loadSourceData();
        this.syncReviewCountLabel();

        if (!this.useYotpo || !this.appKey) {
          this.loading = false;
          this.$nextTick(() => {
            this.updateIndex();
          });
          return;
        }

        this.resolveAdminReviewTotal();
        this.fetchReviews();
      },

      loadSourceData() {
        if (!this.sourceId || !root.document) {
          return;
        }

        const source = root.document.getElementById(this.sourceId);
        if (!source) {
          return;
        }

        try {
          const data = JSON.parse(source.textContent || '{}');
          this.appKey = data.appKey || this.appKey;
          this.yotpoSecret = data.yotpoSecret || this.yotpoSecret;
          this.reviewsUrl = data.reviewsUrl || this.reviewsUrl;
          this.locale = data.locale || this.locale;
          this.useYotpo = data.useYotpo !== false;
          this.useShopAppSummary = data.useShopAppSummary === true;
          this.fallbackRating = data.fallbackRating || this.fallbackRating;
          this.reviewCountTemplate = data.reviewCountTemplate || this.reviewCountTemplate;
          this.reviewCountOneTemplate = data.reviewCountOneTemplate || this.reviewCountOneTemplate;
          this.fallbackDistribution = data.fallbackDistribution || this.fallbackDistribution;
          this.fallbackImageUrl = data.fallbackImageUrl || this.fallbackImageUrl;
          this.headerReviewCountTemplate = data.headerReviewCountTemplate || this.headerReviewCountTemplate;
          this.ratingLabelTemplate = data.ratingLabelTemplate || this.ratingLabelTemplate;
          this.loadingLabel = data.loadingLabel || this.loadingLabel;
          this.syncReviewCountLabel();
        } catch (error) {
          // Keep defaults when config JSON is unavailable.
        }
      },

      formatReviewCountLabel(count) {
        const safeCount = Math.max(0, Number(count) || 0);
        const template = String(
          (safeCount === 1 && this.reviewCountOneTemplate) || this.reviewCountTemplate || '__COUNT__ ratings',
        );

        if (!safeCount) {
          return '';
        }

        if (template.includes('__COUNT__')) {
          return template.replace('__COUNT__', String(safeCount));
        }

        if (template.includes('{{ count }}')) {
          return template.replace('{{ count }}', String(safeCount));
        }

        return `${safeCount} ratings`;
      },

      syncReviewCountLabel() {
        const liveTotal = Number(this.totalReviews) || 0;
        this.displayReviewCountLabel = liveTotal > 0
          ? this.formatReviewCountLabel(liveTotal)
          : '';
      },

      async resolveAdminReviewTotal() {
        const secret = String(this.yotpoSecret || '').trim();
        const yotpo = root.JFTYotpo || {};

        if (!secret || typeof yotpo.fetchAccountReviewTotal !== 'function') {
          return;
        }

        try {
          const total = await yotpo.fetchAccountReviewTotal(this.appKey, secret);
          if (total > 0) {
            this.countSource = 'admin';
            this.totalReviews = Math.max(Number(this.totalReviews) || 0, total);
            this.syncReviewCountLabel();
          }
        } catch (error) {
          // Keep widget total when Admin API is blocked.
        }
      },

      applyPayload(payload) {
        const locale = this.locale;
        const reviews = (payload.reviews || []).map((review) => normalizeReview(
          review,
          locale,
          this.fallbackImageUrl
        ));

        this.reviews = reviews;
        const payloadTotal = Number(payload.totalReviews) || reviews.length;
        if (this.countSource === 'admin') {
          this.totalReviews = Math.max(Number(this.totalReviews) || 0, payloadTotal);
        } else {
          this.totalReviews = payloadTotal;
        }
        this.averageScore = Number(payload.averageScore) || computeAverageScore(reviews);
        this.syncReviewCountLabel();

        const yotpo = root.JFTYotpo || {};
        if (typeof yotpo.resolveDistributionState === 'function') {
          const resolved = yotpo.resolveDistributionState({
            starDistribution: payload.starDistribution || payload.star_distribution,
            averageScore: this.averageScore,
            reviews,
            fallbackPercents: this.fallbackDistribution,
          });
          this.distribution = resolved.distribution;
          this.fallbackDistribution = resolved.fallbackPercents;
        } else {
          const parsedDistribution = parseStarDistribution(
            payload.starDistribution || payload.star_distribution
          );

          if (parsedDistribution) {
            this.distribution = parsedDistribution;
          } else {
            this.distribution = buildDistribution(reviews);
          }
        }
      },

      async fetchReviews() {
        if (!this.appKey) {
          this.loading = false;
          this.loadError = true;
          return;
        }

        const yotpo = root.JFTYotpo || {};

        try {
          let reviews = [];
          let bottomline = {};
          let pagination = {};

          if (yotpo.fetchReviewsPage) {
            const result = await yotpo.fetchReviewsPage(this.appKey, { page: 1, perPage: 50 });
            reviews = result.reviews;
            bottomline = result.bottomline || {};
            pagination = result.pagination || {};
          } else {
            const response = await fetch(
              `https://api.yotpo.com/v1/widget/${encodeURIComponent(this.appKey)}/reviews.json?per_page=50&page=1`
            );

            if (!response.ok) {
              throw new Error(`Yotpo request failed: ${response.status}`);
            }

            const data = await response.json();
            const responseBody = data?.response || {};
            reviews = Array.isArray(responseBody.reviews) ? responseBody.reviews : [];
            bottomline = responseBody.bottomline || {};
            pagination = responseBody.pagination || {};
          }

          const normalized = reviews.map((review) => normalizeReview(
            review,
            this.locale,
            this.fallbackImageUrl
          ));
          const paginationTotal = Number(pagination.total) || 0;
          const bottomlineTotal = Number(
            bottomline.total_review
            ?? bottomline.totalReview
          ) || 0;
          const totalReviews = bottomlineTotal || paginationTotal || normalized.length;
          const averageScore = Number(
            bottomline.average_score
            ?? bottomline.averageScore
            ?? computeAverageScore(reviews)
          ) || 0;

          this.reviews = normalized;
          if (this.countSource !== 'admin') {
            this.totalReviews = totalReviews;
          } else {
            this.totalReviews = Math.max(Number(this.totalReviews) || 0, totalReviews);
          }
          this.averageScore = averageScore;
          this.syncReviewCountLabel();

          if (typeof yotpo.resolveDistributionState === 'function') {
            const resolved = yotpo.resolveDistributionState({
              starDistribution: bottomline.star_distribution || bottomline.starDistribution,
              averageScore,
              reviews: normalized,
              fallbackPercents: this.fallbackDistribution,
            });
            this.distribution = resolved.distribution;
            this.fallbackDistribution = resolved.fallbackPercents;
          } else {
            const parsedDistribution = parseStarDistribution(
              bottomline.star_distribution || bottomline.starDistribution
            );

            if (parsedDistribution) {
              this.distribution = parsedDistribution;
            } else {
              this.distribution = buildDistribution(reviews);
            }
          }

          this.loadError = false;
        } catch (error) {
          this.loadError = true;
        } finally {
          this.loading = false;
          this.$nextTick(() => {
            this.updateIndex();
          });
        }
      },

      get yotpoReady() {
        return this.useYotpo && !this.loadError && this.cardReviews.length > 0;
      },

      get yotpoSummaryReady() {
        return this.useYotpo && !this.loading && !this.loadError && (this.totalReviews > 0 || this.averageScore > 0);
      },

      get summaryReady() {
        if (this.useShopAppSummary) {
          return true;
        }

        return this.yotpoSummaryReady;
      },

      get cardReviews() {
        const withMedia = this.reviews.filter((review) => review.hasMedia);

        if (withMedia.length > 0) {
          return withMedia;
        }

        return this.reviews;
      },

      get hasReviews() {
        return this.totalReviews > 0 && this.reviews.length > 0;
      },

      get averageScoreDisplay() {
        if (this.totalReviews <= 0 && this.averageScore <= 0) {
          return '0.0';
        }

        return Number(this.averageScore).toFixed(1);
      },

      get summaryRatingDisplay() {
        if (this.useShopAppSummary) {
          return this.fallbackRating;
        }

        if (this.yotpoSummaryReady) {
          return this.averageScoreDisplay;
        }

        return this.fallbackRating;
      },

      get summaryReviewsLabel() {
        const liveTotal = Number(this.totalReviews) || 0;
        if (liveTotal > 0) {
          return this.formatReviewCountLabel(liveTotal);
        }

        return this.displayReviewCountLabel || '';
      },

      get summaryRatingLabel() {
        return this.ratingLabelTemplate
          .replace('__RATING__', String(this.summaryRatingDisplay))
          .replace('{{ rating }}', String(this.summaryRatingDisplay));
      },

      summaryStarFill(star) {
        const rating = Number(this.summaryRatingDisplay) || 0;
        const fill = rating - (Number(star) - 1);

        if (fill <= 0) {
          return 0;
        }

        if (fill >= 1) {
          return 100;
        }

        return Math.round(fill * 100);
      },

      get displayReviewCount() {
        return Number(this.totalReviews) || 0;
      },

      get distributionRows() {
        if (this.useShopAppSummary) {
          return buildDistributionRows({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }, this.fallbackDistribution);
        }

        if (this.yotpoSummaryReady) {
          return buildDistributionRows(this.distribution, this.fallbackDistribution);
        }

        return buildDistributionRows({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }, this.fallbackDistribution);
      },

      get headerReviewCountLabel() {
        return this.headerReviewCountTemplate
          .replace('__COUNT__', String(this.displayReviewCount))
          .replace('{{ count }}', String(this.displayReviewCount));
      },

      reviewRatingLabel(score) {
        return this.ratingLabelTemplate
          .replace('__RATING__', String(score))
          .replace('{{ rating }}', String(score));
      },

      get track() {
        if (this.yotpoReady) {
          return this.$refs.yotpoTrack || null;
        }

        return this.$refs.reviewTrack || this.$refs.fallbackTrack || null;
      },

      get cards() {
        const track = this.track;

        if (!track) {
          return [];
        }

        return Array.from(
          track.querySelectorAll(
            '.home-testimonials__review-card, .about-testimonials__review-card',
          ),
        );
      },

      updateIndex() {
        const track = this.track;
        const cards = this.cards;

        if (!track || cards.length === 0) {
          this.activeIndex = 0;
          this.canNext = false;
          this.canPrev = false;
          return;
        }

        const trackRect = track.getBoundingClientRect();
        let nearestIndex = 0;
        let nearestDistance = Number.POSITIVE_INFINITY;

        cards.forEach((card, index) => {
          const cardRect = card.getBoundingClientRect();
          const distance = Math.abs(cardRect.left - trackRect.left);

          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestIndex = index;
          }
        });

        this.activeIndex = nearestIndex;
        this.canNext = nearestIndex < cards.length - 1;
        this.canPrev = nearestIndex > 0;
      },

      prev() {
        const track = this.track;
        const cards = this.cards;
        const prevIndex = Math.max(this.activeIndex - 1, 0);
        const prevCard = cards[prevIndex];

        if (!track || !prevCard) {
          return;
        }

        track.scrollTo({
          left: prevCard.offsetLeft - track.offsetLeft,
          behavior: 'smooth',
        });

        this.activeIndex = prevIndex;
        this.canPrev = prevIndex > 0;
        this.canNext = prevIndex < cards.length - 1;
      },

      next() {
        const track = this.track;
        const cards = this.cards;
        const nextIndex = Math.min(this.activeIndex + 1, cards.length - 1);
        const nextCard = cards[nextIndex];

        if (!track || !nextCard) {
          return;
        }

        track.scrollTo({
          left: nextCard.offsetLeft - track.offsetLeft,
          behavior: 'smooth',
        });

        this.activeIndex = nextIndex;
        this.canNext = nextIndex < cards.length - 1;
        this.canPrev = nextIndex > 0;
      },

    }));
  }

  if (root.document) {
    root.document.addEventListener('alpine:init', registerHomeYotpoTestimonials);

    if (root.Alpine) {
      registerHomeYotpoTestimonials();
    }
  }
})();
